<template>
  <div class="flex h-full flex-col bg-white">
    <div class="flex items-center justify-between px-8 py-4" style="background-color: #E2E8F0;">
      <div class="text-lg font-semibold uppercase text-black">
        {{ metric.name }}
      </div>
      <div class="text-slate-500 transition hover:text-slate-700 cursor-pointer" @click="$emit('close')">
        <Icon icon-name="CloseIcon" />
      </div>
    </div>
    <div class="flex-1 space-y-8 bg-white py-6">

      <div class="rounded-lg border border-slate-200 relative mx-8">
        <div
          class="absolute badge-dot inline-flex items-center bg-gray-600 border border-slate-500 px-5 py-2 text-xs font-semibold text-white">
          <span>User Potential</span>
        </div>
        <div class="px-8 pt-10 pb-6 space-y-4">
          <label class="text-sm font-semibold text-slate-800 block">User Potential</label>
          <div class="flex flex-col sm:flex-row gap-3">
            <input
              v-model="userPotential"
              type="number"
              min="0"
              placeholder="Enter Number"
              class="w-full rounded border border-slate-300 px-4 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              type="button"
              class="inline-flex items-center justify-center rounded bg-blue-500 px-6 py-2 text-sm font-semibold text-white transition hover:bg-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
              @click="updateUserPotential"
            >
              Update
            </button>
          </div>
          <p v-if="userPotentialError" class="text-xs text-red-500">{{ userPotentialError }}</p>
        </div>
      </div>



      <div class="rounded-lg border border-slate-200 relative mx-8">
        <div
          class="absolute badge-dot inline-flex items-center bg-gray-600 border border-slate-500 px-5 py-2 text-xs font-semibold text-white">
          <span>{{ metric.name }}</span>
        </div>
        <div class="mb-8">
          <div class="flex flex-wrap items-center justify-between gap-3 px-8 mt-8">
            <div class="w-full">
              <ProgressBar
                :value="progressValue"
                :label="progressLabel"
                :primary-percent="progressPrimaryPercent"
                :secondary-percent="progressSecondaryPercent"
                primary-color="#22C55E"
                secondary-color="#38BDF8"
              />
            </div>
            <h3 class="text-sm font-semibold text-slate-800 mt-4">Summary</h3>
          </div>
          <div class="mx-8">
            <div class="grid grid-cols-3 gap-4 px-6 py-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600">
              <span>Status</span>
              <span>{{ columnHeaders.date }}</span>
              <span>{{ columnHeaders.value }}</span>
            </div>
            <div class="bg-white">
              <div v-for="detail in computedDetails" :key="detail.id"
                class=" text-xs relative grid grid-cols-3 items-center gap-4 px-6 py-4 mb-2  bg-gray-100 rounded-lg font-semibold text-neutral-800">
                <span class="absolute left-0 top-0 h-full w-1 rounded-l bg-green-500"></span>

                <div>
                  <span>{{ detail.status }}</span>
                </div>

                <div>
                  {{ detail.date }}
                </div>
                <div>
                  {{ detail.sharedUsers }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

</template>

<script>
import Icon from "../core/Icon.vue";
import ProgressBar from "../core/ProgressBar.vue";
export default {
  name: "UserEmailDrawerContent",
  components: {
    Icon,
    ProgressBar
  },
  data() {
    return {
      details: [
      ],
      userPotential: "",
      userPotentialError: ""
    }
  },
  props: {
    metric: {
      type: Object,
      default: null
    },
    initialPotentialUsers: {
      type: [Number, String],
      default: null
    }
  },
  created() {
    if (this.initialPotentialUsers != null) {
      this.userPotential = this.initialPotentialUsers;
    }
  },
  computed: {
    tenantFeatureUsage() {
      return this.$store?.state?.TenantAnalyticsStore?.tenantFeatureUsage || null;
    },
    tenantBasicInfo() {
      return this.$store?.state?.TenantAnalyticsStore?.tenantBasicInfo || null;
    },
    isUsersMetric() {
      return (this.metric?.name || "").toString().toLowerCase() === "users";
    },
    isEmailMetric() {
      const normalized = (this.metric?.name || "").toString().toLowerCase();
      return normalized === "email inbox" || normalized === "email outbox";
    },
    userAddedActivities() {
      if (!this.isUsersMetric) {
        return [];
      }

      const activities = this.tenantFeatureUsage?.UserAddedData?.userAddedActivitiesByDate;
      if (!Array.isArray(activities)) {
        return [];
      }

      return activities.map((activity, index) => {
        const count = activity?.count ?? 0;
        return {
          id: activity?.id ?? `user-activity-${index}`,
          status: activity?.label ?? "Added",
          date: this.formatDate(activity?.date),
          sharedUsers: this.formatUserCount(count)
        };
      });
    },
    computedDetails() {
      if (this.isUsersMetric) {
        const userDetails = this.userAddedActivities;
        return userDetails.length ? userDetails : this.details;
      }

      if (this.isEmailMetric) {
        const emailDetails = this.emailUsageDetailEntries;
        return emailDetails.length ? emailDetails : this.details;
      }

      return this.details;
    },
    progressValue() {
      if (this.isUsersMetric) {
        return Number(this.metric?.value) || 0;
      }

      if (this.isEmailMetric) {
        const fromStore = Number(this.emailUsageTotals.totalUsed);
        if (Number.isFinite(fromStore) && fromStore > 0) {
          return fromStore;
        }
        return Number(this.metric?.value) || 0;
      }

      return this.metric?.value ?? 0;
    },
    progressTotal() {
      if (this.isUsersMetric) {
        return Number(this.metric?.total) || 0;
      }

      if (this.isEmailMetric) {
        const fromStore = Number(this.emailUsageTotals.totalAvailable);
        if (Number.isFinite(fromStore) && fromStore > 0) {
          return fromStore;
        }
        return Number(this.metric?.total) || 0;
      }

      return Number(this.metric?.total) || 0;
    },
    progressLabel() {
      if (this.isUsersMetric) {
        const active = this.progressValue;
        const total = this.progressTotal;
        if (!total) {
          return `${active} User${active === 1 ? "" : "s"}`;
        }
        return `Active/ ${total} User${total === 1 ? "" : "s"}`;
      }

      if (this.isEmailMetric) {
        const used = this.progressValue;
        const total = this.progressTotal;
        if (!total) {
          return this.metric?.secondary || `${this.formatNumber(used)} Used`;
        }
        return this.metric?.secondary || `Used/ ${this.formatNumber(total)} Emails`;
      }

      return this.metric?.secondary || "";
    },
    progressPrimaryPercent() {
      const total = this.progressTotal;
      if (!total) {
        return 0;
      }
      const percent = (this.progressValue / total) * 100;
      return Math.max(0, Math.min(100, Number.isFinite(percent) ? Math.round(percent) : 0));
    },
    progressSecondaryPercent() {
      const remaining = 100 - this.progressPrimaryPercent;
      return remaining > 0 ? remaining : 0;
    },
    emailUsageTotals() {
      if (!this.isEmailMetric) {
        return { totalUsed: 0, totalAvailable: 0 };
      }

      const key = (this.metric?.name || "").toLowerCase().includes("outbox") ? "outbox" : "inbox";
      const usage = this.tenantBasicInfo?.emailUsage?.[key] || {};

      const totalUsed = usage.total_used ?? usage.details?.value ?? 0;
      const totalAvailable = usage.total_available ?? usage.details?.limit ?? 0;

      return {
        totalUsed,
        totalAvailable,
        usage
      };
    },
    emailUsageDetailEntries() {
      if (!this.isEmailMetric) {
        return [];
      }

      const { usage } = this.emailUsageTotals;
      if (!usage || Object.keys(usage).length === 0) {
        return [];
      }

      const detail = usage.details || {};
      const featureName = detail.feature_name || this.metric?.name || "-";
      const detailValue = detail.value != null ? `${this.formatNumber(detail.value)} Emails` : "-";

      return [
        {
          id: `email-${featureName}`,
          status: this.formatEmailFeatureLabel(featureName),
          date: this.formatDate(detail.created_at),
          sharedUsers: detailValue
        }
      ];
    },
    columnHeaders() {
      if (this.isEmailMetric) {
        return {
          date: "Date",
          value: "Value"
        };
      }

      return {
        date: "Date and Time",
        value: "Users"
      };
    }
  },
  methods: {
    formatDate(value) {
      if (!value) {
        return "-";
      }

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }

      return date.toLocaleString();
    },
    formatUserCount(value) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric) || numeric < 0) {
        return "-";
      }
      const label = numeric === 1 ? "User" : "Users";
      return `${numeric} ${label}`;
    },
    formatNumber(value) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) {
        return "-";
      }
      return new Intl.NumberFormat().format(numeric);
    },
    formatEmailFeatureLabel(featureName) {
      if (!featureName) {
        return "-";
      }
      const label = featureName.toString().replace(/[_-]/g, " ");
      return label.charAt(0).toUpperCase() + label.slice(1);
    },
    closeDrawer() {
      this.$emit('close');
    },
    async updateUserPotential() {
      const numeric = Number(this.userPotential);
      if (!Number.isFinite(numeric) || numeric < 0) {
        this.userPotentialError = "Please enter a valid non-negative number.";
        return;
      }
      this.userPotentialError = "";
      const tenantId = this.$route?.params?.id;
      if (!tenantId) {
        this.userPotentialError = "Tenant ID is missing.";
        return;
      }
      try {
        await this.$store.dispatch(
          "TenantAnalyticsStore/updatePotentialUsers",
          { tenantId, potentialUsers: numeric }
        );
        this.$toasted?.success("Potential users updated successfully.");
      } catch (error) {
        this.userPotentialError =
          error?.response?.data?.message || "Failed to update potential users.";
      }
    }
  }
};
</script>
<style scoped>
.badge-dot {
  top: -17px;
  border-radius: 3px;
  left: 20px;
}
</style>